// Feb 2026 — Admin-only view of the Transferable Skills Reflection
// anonymous aggregate tallies. Privacy spec is strict: counts only,
// never per-person. Just lists each question's options ranked by how
// often teachers chose them, plus Part 1 distributions and averages.
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Download, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RankedTable({ rows, testid, title }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-sm text-[#4a6984]/70" data-testid={`${testid}-empty`}>
        No selections recorded yet.
      </div>
    );
  }
  const max = rows[0]?.count || 0;
  return (
    <div data-testid={testid}>
      {title ? (
        <h4 className="text-xs uppercase tracking-[0.18em] font-extrabold text-[#1c2d3d] mb-2">
          {title}
        </h4>
      ) : null}
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70 font-extrabold">
          <tr>
            <th className="text-left py-1 font-extrabold">Option</th>
            <th className="text-right py-1 font-extrabold w-16">Count</th>
            <th className="text-right py-1 font-extrabold w-16">%</th>
            <th className="text-left py-1 font-extrabold w-32 pl-3">Bar</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.option_id} className="border-t border-[#4a6984]/10">
              <td className="py-1.5 text-[#1c2d3d] font-medium pr-3">{r.text}</td>
              <td className="py-1.5 text-right text-[#1c2d3d] font-extrabold tabular-nums">
                {r.count}
              </td>
              <td className="py-1.5 text-right text-[#4a6984] tabular-nums">
                {r.percent.toFixed(1)}%
              </td>
              <td className="py-1.5 pl-3">
                <div
                  className="h-1.5 rounded-full bg-[#3EB489]"
                  style={{ width: max ? `${(r.count / max) * 100}%` : "0%" }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Part1SkillBlock({ skill }) {
  return (
    <div
      className="ae-card p-5"
      data-testid={`reflection-aggregate-part1-${skill.skill_id}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-extrabold text-base text-[#1c2d3d]">{skill.label}</h3>
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/70">
          Skill avg
          <span className="ml-2 text-[#1c2d3d] tabular-nums text-sm">
            {skill.skill_average !== null ? skill.skill_average.toFixed(2) : "—"}
          </span>
          <span className="ml-3">
            {skill.responses_total} response{skill.responses_total === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70 font-extrabold">
          <tr>
            <th className="text-left py-1 font-extrabold">Item</th>
            <th className="text-right py-1 font-extrabold w-12">1</th>
            <th className="text-right py-1 font-extrabold w-12">2</th>
            <th className="text-right py-1 font-extrabold w-12">3</th>
            <th className="text-right py-1 font-extrabold w-12">4</th>
            <th className="text-right py-1 font-extrabold w-14">Avg</th>
            <th className="text-right py-1 font-extrabold w-14">N</th>
          </tr>
        </thead>
        <tbody>
          {skill.items.map((item) => (
            <tr key={item.item_id} className="border-t border-[#4a6984]/10">
              <td className="py-1.5 text-[#1c2d3d] pr-3 text-[13px]">{item.text}</td>
              {[1, 2, 3, 4].map((v) => (
                <td
                  key={v}
                  className="py-1.5 text-right text-[#1c2d3d] tabular-nums"
                >
                  {item.distribution[String(v)] || 0}
                </td>
              ))}
              <td className="py-1.5 text-right text-[#1c2d3d] font-extrabold tabular-nums">
                {item.average !== null ? item.average.toFixed(2) : "—"}
              </td>
              <td className="py-1.5 text-right text-[#4a6984] tabular-nums">
                {item.responses}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReflectionAggregatePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/reflection-aggregate`, {
        withCredentials: true,
      });
      setData(d);
      setError("");
    } catch (e) {
      setError(
        e?.response?.status === 401 || e?.response?.status === 403
          ? "You don't have permission to view this."
          : "Couldn't load aggregate — try again.",
      );
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDownloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/reflection-aggregate.csv`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "atomicedu_reflection_aggregate.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // swallow
    }
  };

  if (loading) {
    return (
      <div
        className="ae-card p-6 inline-flex items-center gap-2 text-[#4a6984]"
        data-testid="reflection-aggregate-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        className="ae-card p-6 text-sm text-rose-700"
        data-testid="reflection-aggregate-error"
      >
        {error || "No data."}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reflection-aggregate-panel">
      <div className="ae-card p-5 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70">
            Anonymous · counts only · never linked to a teacher
          </p>
          <p className="mt-1 font-black text-xl text-[#1c2d3d] tracking-tight">
            {data.submissions_total} submission
            {data.submissions_total === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={load}
            disabled={reloading}
            data-testid="reflection-aggregate-refresh"
            className="h-9 px-3 rounded-md text-xs font-extrabold inline-flex items-center gap-1.5 bg-white border border-[#4a6984]/25 text-[#1c2d3d] hover:bg-[#f4f5f7]"
          >
            {reloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
          <Button
            type="button"
            onClick={handleDownloadCsv}
            data-testid="reflection-aggregate-csv"
            className="h-9 px-3 rounded-md text-xs font-extrabold inline-flex items-center gap-1.5 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c]"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
        </div>
      </div>

      {/* Part 1 — per-skill distributions */}
      <section data-testid="reflection-aggregate-part1">
        <h3 className="font-black text-lg text-[#1c2d3d] mb-3">
          Part 1 — this year, skill by skill
        </h3>
        <div className="space-y-4">
          {data.part1.map((s) => (
            <Part1SkillBlock key={s.skill_id} skill={s} />
          ))}
        </div>
      </section>

      {/* Part 2 */}
      <section
        className="ae-card p-5 space-y-5"
        data-testid="reflection-aggregate-part2"
      >
        <h3 className="font-black text-lg text-[#1c2d3d]">
          Part 2 — looking back
        </h3>
        <RankedTable
          title="Skills that showed up most naturally"
          testid="reflection-aggregate-most-natural"
          rows={data.part2.most_natural}
        />
        <RankedTable
          title="Skills that were hardest to make visible"
          testid="reflection-aggregate-hardest-visible"
          rows={data.part2.hardest_visible}
        />
        <RankedTable
          title="What got in the way most"
          testid="reflection-aggregate-got-in-way"
          rows={data.part2.got_in_way}
        />
      </section>

      {/* Part 3 */}
      <section
        className="ae-card p-5 space-y-5"
        data-testid="reflection-aggregate-part3"
      >
        <h3 className="font-black text-lg text-[#1c2d3d]">
          Part 3 — looking forward
        </h3>
        <RankedTable
          title="The one skill to build in from week one"
          testid="reflection-aggregate-one-skill"
          rows={data.part3.one_skill}
        />
        <RankedTable
          title="What would make that real"
          testid="reflection-aggregate-make-real"
          rows={data.part3.make_real}
        />
        <RankedTable
          title="What support would actually help"
          testid="reflection-aggregate-support"
          rows={data.part3.support}
        />
      </section>
    </div>
  );
}
